/* eslint-disable no-param-reassign */
var domFunctions = require('../utils/_domFunctions');
var urlUtils = require('../utils/_urlUtils');
var formCheck = require('../forms/check/_index');

/**
 * This molecule handle the change of country in the country select in address forms. When changing the country, a back-end call
 * is made to update the count of mandatory fields, to get the name of new mandatory fields and fields that are no more mandatories,
 * and the informations for the states field. It updates and validates the address form accordingly.
 * Back-end endpoint : Address-ChangeCountry
 * Used to : billing address form, add/edit address form in address book
 */
class mSelectCountry extends HTMLSelectElement {
    constructor() {
        super();
        this.changeUrl = this.dataset.changeUrl;
        this.form = this.closest('form');
        this.titleInput = this.form.querySelector('#' + this.form.dataset.sourcename + 'Title');
        this.currentCountry = this.value;
        this.states = this.form.querySelector('[data-states]');
        this.fieldsToClean = ['address1', 'address2', 'postalCode', 'stateCode', 'city', 'postalCodePart1', 'postalCodePart2'];
        this.validateFields = false;
        this.jpPostalCode = this.form.querySelector('[is="m-form-postal-code"]');
        this.noClean = 'noClean' in this.dataset;
        this.noChange = false;
    }

    connectedCallback() {
        this.addEventListener('change', () => {
            if (this.noChange) {
                this.noChange = false;
                return;
            }
            if (this.currentCountry !== this.value) {
                this.validateFields = true;
                this.changeCountry();
                if (!this.noClean) {
                    this.cleanFields();
                }
                this.currentCountry = this.value;
            }
        });
        // This event is used to handle the change of country made by m-billing-same-shipping. In this case we don't want to
        // clean the fields and update the phone numbers because this is already done by m-billing-same-shipping
        this.addEventListener('checkout:changeCountry', () => {
            if (this.currentCountry !== this.value) {
                this.validateFields = false;
                this.changeCountry();
                this.currentCountry = this.value;
            } else {
                this.closest('form').dispatchEvent(new CustomEvent('address:changeCountryDone'));
            }
            // to trigger the change event (for m-combobox purposes) without cleaning fields
            this.noChange = true;
            this.dispatchEvent(new Event('change'));
        });
        // Updates the form with the actual country at the page's load (all the fields are shown by default,
        // this is this call that will prepare the form for the actual country)
        this.changeCountry();
    }

    /**
     * Call the back-end and updates the form fields accordingly
     */
    changeCountry() {
        var country = this.value;
        var urlParams = {
            countryCode: country
        };

        if (this.changeUrl) {
            let url = urlUtils.appendToUrl(this.changeUrl, urlParams);

            fetch(url, {
                method: 'GET'
            }).then((response) => {
                return response.json();
            }).then((responseData) => {
                if (responseData && responseData.specificsMandatoryFields) {
                    var data = responseData.specificsMandatoryFields;
                    this.form.mandatoryFields = data.cpt;
                    this.form.extendedData.mandatory = data.cpt;
                    var addressSuggestions = this.form.querySelector('[is="m-address-suggestions"]');
                    addressSuggestions.dispatchEvent(new CustomEvent('autocomplete:toggle', { detail: { autocompleteEnabled: responseData.gmapEnabled } }));
                    if (responseData.fieldsRules) {
                        this.updateFieldsRules(responseData.fieldsRules);
                    }
                    this.updatePostalCodePattern(responseData.patternPostalCode);
                    data.fieldsNotRequired.forEach(field => {
                        if (field.includes('gender')) {
                            this.toggleTitle(true);
                        }
                        this.hideField(field);
                    });
                    data.fieldsToRequire.forEach(field => {
                        var isGender = field.includes('gender');
                        this.showField(field, isGender);
                    });
                    if (responseData.postalCodeJp !== null && this.jpPostalCode) {
                        this.jpPostalCode.toggleDisplay(responseData.postalCodeJp);
                    }
                    this.displayStates(responseData);
                    this.closest('form').dispatchEvent(new CustomEvent('address:changeCountryDone'));
                }
            });
        }
    }

    /**
     * Update postal code pattern according to country
     * @param {string|null} postalCodePattern postal code pattern
     */
    updatePostalCodePattern(postalCodePattern) {
        var field = this.form.querySelector('[name$="postalCode"]');
        if (postalCodePattern) {
            field.pattern = postalCodePattern;
        } else {
            field.removeAttribute('pattern');
        }
    }

    updateFieldsRules(fieldsRules) {
        var fieldIds = Object.keys(fieldsRules);
        for (var fieldId of fieldIds) {
            var field = this.form.querySelector('[name$="' + fieldId + '"]');
            if (field) {
                var fieldRules = fieldsRules[fieldId];
                for (var attribute of Object.keys(fieldRules)) {
                    if (fieldRules[attribute]) {
                        field[attribute] = fieldRules[attribute];
                    } else {
                        field.removeAttribute(attribute);
                    }
                }

                this.closest('form').dispatchEvent(new CustomEvent('form:validateField', {
                    detail: {
                        field: field,
                        allowEmpty: true,
                        triggerInput: true
                    }
                }));
            }
        }
    }

    /**
     * Clean fields related to the address
     */
    cleanFields() {
        this.fieldsToClean.forEach(fieldName => {
            var field = this.form.querySelector('[name$="' + fieldName + '"]');
            if (field) {
                formCheck.resetFieldValue(field);
            }
        });
    }

    /**
     * Show a specific field in the form, make it required and trigger the validation (if this.validateFields is true, an empty value will not result in an error)
     * @param {string} fieldName the name of the field
     * @param {boolean} isGender is gender field
     */
    showField(fieldName, isGender) {
        var field = this.form.querySelector('[name="' + fieldName + '"]');
        if (field) {
            var formField = field.closest('.form-field');
            formField.classList.remove('d-none');
            this.setRequired(field, true);
            if (this.validateFields && !isGender) {
                this.closest('form').dispatchEvent(new CustomEvent('form:validateField', {
                    detail: {
                        field: field,
                        allowEmpty: true
                    }
                }));
            }
        }
        if (isGender) {
            this.toggleTitle(false);
        }
    }

    /**
     * Hide a specific field in the form and remove the required
     * @param {string} fieldName the name of the field
     */
    hideField(fieldName) {
        var field = this.form.querySelector('[name="' + fieldName + '"]');
        if (field) {
            formCheck.resetFieldValue(field);
            var formField = field.closest('.form-field');
            formField.classList.add('d-none');
            this.setRequired(field, false);
        }
    }

    toggleTitle(show) {
        var titleField = this.titleInput.closest('.form-field');
        if (show) {
            titleField.classList.remove('d-none');
        } else {
            titleField.classList.add('d-none');
        }
        if (this.titleInput.value) {
            this.titleInput.dispatchEvent(new Event('change'));
        }
    }

    /**
     * Update the states field according to the back-end call result
     * @param {Object} data data returned by server
     */
    displayStates(data) {
        var formField = this.states.closest('.form-field');
        if (data.displayStates && data.renderedStates && formField) {
            formField.classList.remove('d-none');
            domFunctions.replaceHtml(this.states, data.renderedStates);
            this.states.value = this.states.dataset.stateValue;
            this.states.dispatchEvent(new Event('change'));
            this.setRequired(this.states, true);
            if (this.validateFields) {
                this.closest('form').dispatchEvent(new CustomEvent('form:validateField', {
                    detail: {
                        field: this.states,
                        allowEmpty: true
                    }
                }));
            }
        } else if (formField) {
            formCheck.resetFieldValue(this.states);
            formField.classList.add('d-none');
            domFunctions.emptyNode(this.states);
            this.setRequired(this.states, false);
        }
    }

    /**
     * Update the mandatoriness of a field
     * @param {*} node the actual field
     * @param {*} value if the field is mandatory
     */
    setRequired(node, value) {
        node.ariaRequired = value.toString();
        node.required = value;
    }

}

customElements.define('m-select-country', mSelectCountry, {
    extends: 'select'
});
